import { EditorContent, useEditor } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import * as R from 'ramda'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { Typography } from '@material-ui/core'
import BorderAllIcon from '@material-ui/icons/BorderAll'
import CodeIcon from '@material-ui/icons/Code'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import FormatAlignCenterIcon from '@material-ui/icons/FormatAlignCenter'
import FormatAlignLeftIcon from '@material-ui/icons/FormatAlignLeft'
import FormatAlignRightIcon from '@material-ui/icons/FormatAlignRight'
import FormatAlignJustifyIcon from '@material-ui/icons/FormatAlignJustify'
import FormatBoldIcon from '@material-ui/icons/FormatBold'
import FormatClearIcon from '@material-ui/icons/FormatClear'
import FormatColorTextOutlinedIcon from '@material-ui/icons/FormatColorTextOutlined'
import FormatIndentIncreaseIcon from '@material-ui/icons/FormatIndentIncrease'
import FormatItalicIcon from '@material-ui/icons/FormatItalic'
import FormatLineSpacingIcon from '@material-ui/icons/FormatLineSpacing'
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted'
import FormatListNumberedIcon from '@material-ui/icons/FormatListNumbered'
import FormatQuoteOutlinedIcon from '@material-ui/icons/FormatQuoteOutlined'
import FormatUnderlinedIcon from '@material-ui/icons/FormatUnderlined'
import ImageOutlinedIcon from '@material-ui/icons/ImageOutlined'
import LinkIcon from '@material-ui/icons/Link'
import LinkOffOutlinedIcon from '@material-ui/icons/LinkOffOutlined'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import PostAddOutlinedIcon from '@material-ui/icons/PostAddOutlined'
import RedoIcon from '@material-ui/icons/Redo'
import RemoveIcon from '@material-ui/icons/Remove'
import UndoIcon from '@material-ui/icons/Undo'
import VideocamOutlinedIcon from '@material-ui/icons/VideocamOutlined'
import OutlinedButtons from 'app/componentV4/Button/OutlinedButtons'
import MessagespringLoading from 'app/componentV4/Loader/MessagespringLoading'
import apiConfigs from 'app/configs/apiConfigs'
import appInfo from 'app/configs/appInfo'
import { CLOSE } from 'app/constants/button'
import clsx from 'clsx'
import { usePlace } from 'commons/usePlace'
import { isEmptyOrNil } from 'libs/empty'
import { FormattedMessage, useIntl } from 'react-intl'
import SimpleTiptapDropdown from './TiptapDropdown'
import {
  applyImageStyling,
  fontSizeOptions,
  getFontFamily,
  lineHeightOptions,
  removeImageKitStyle,
  VALIDATION_CONFIG,
} from './TiptapUtils'
import {
  generateBodyImageId,
  useEditorExtensions,
  useEditorProps,
  useFontSizeExtension,
  useNoTranslateMark,
  useToolbarHandlers,
  useVideoExtension,
} from './useTiptapEditor'
import { useAccessToken } from 'app/auth/authHooks'
const ZERO_WIDTH_SPACE = '\u200B'
// Memoized Components
const ToolbarButton = React.memo(
  ({
    onClick,
    isActive,
    title,
    children,
    className = '',
    disabled = false,
  }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      className={`flex p-2 m-1 rounded items-center ${isActive ? 'bg-primary-350 text-secondary' : 'hover:bg-gray-200 text-grey-400'} ${className}`}
      title={title}
    >
      {children}
    </button>
  ),
)

const TableBubbleMenu = React.memo(({ editor, handleAddColumnAfter }) => (
  <BubbleMenu
    editor={editor}
    shouldShow={({ editor }) => editor.isActive('table')}
    options={{
      placement: 'top',
      offset: 8,
    }}
  >
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-2 flex gap-1">
      <button
        type="button"
        onClick={handleAddColumnAfter}
        className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
        title="Add Column After"
      >
        +Col
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().addRowAfter().run()}
        className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
        title="Add Row After"
      >
        +Row
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().deleteColumn().run()}
        className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
        title="Delete Column"
      >
        -Col
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().deleteRow().run()}
        className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
        title="Delete Row"
      >
        -Row
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().deleteTable().run()}
        className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
        title="Delete Table"
      >
        ×Table
      </button>
    </div>
  </BubbleMenu>
))

const TiptapEditor = ({
  name,
  value,
  placeholder,
  className,
  maxLength,
  onUploading,
  onUploaded,
  onChangeImage,
  toolbarItems,
  onChange,
  onError,
  onFocus,
  error,
  errorText,
  onEditorReady,
  onBodyImageAdded,
  onBodyImageRemoved,
  ...otherProps
}) => {
  // const [field, meta, helpers] = useField(name)
  const place = usePlace()
  const [isLoading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState(undefined)
  const [showHtmlSource, setShowHtmlSource] = useState(false)
  const accessToken = useAccessToken()

  const { formatMessage } = useIntl()
  // const formik = useFormikContext()

  // imageInfo is not use but i want to use preview function of useState in setImageInfo
  // const [imageInfo, setImageInfo] = useState([])
  // Custom hooks
  const NoTranslate = useNoTranslateMark()
  const FontSizeStyle = useFontSizeExtension()
  const VideoExtension = useVideoExtension()
  const extensions = useEditorExtensions(
    NoTranslate,
    FontSizeStyle,
    VideoExtension,
  )

  // Memoized update handler
  const handleUpdate = useCallback(
    ({ editor }) => {
      const content = editor.getHTML()
      onChange(content)
      // helpers.setValue(content, false) // Don't mark as touched

      // Trigger validation if field was previously touched and had error
      if (error) {
        onError(undefined)
      }
    },
    [error, onError, onChange],
  )

  // Handle editor focus to mark field as touched
  const handleEditorFocus = useCallback(() => {
    if (onFocus) {
      onFocus()
    }
  }, [onFocus])

  // Create editor with basic props first
  const baseEditorProps = useEditorProps()

  const onEditorLoading = (value) => {
    setLoading(value)
  }

  const setEditorErrorMessage = (messsage) => {
    setErrorMessage(messsage)
  }

  const editor = useEditor({
    extensions,
    content: value || '',
    editable: !isLoading,
    onUpdate: handleUpdate,
    onFocus: handleEditorFocus,
    editorProps: baseEditorProps,
    shouldRerenderOnTransaction: false,
  })
  const isApplyingBodyImageIdsRef = useRef(false)

  const normalizeBodyImageUrl = useCallback((src) => {
    if (!src) return src
    const cdnUrl = apiConfigs.cndUrl?.replace(/\/$/, '')
    if (!cdnUrl) return src
    if (src.startsWith(`${cdnUrl}/`)) return src.slice(cdnUrl.length + 1)
    if (src.startsWith(cdnUrl))
      return src.slice(cdnUrl.length).replace(/^\/+/, '')
    return src
  }, [])

  useEffect(() => {
    if (!editor || !value) return

    const current = editor.getHTML()
    if (current !== value) {
      editor.commands.setContent(value, false)
    }
  }, [value, editor])

  useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(editor)
    }
  }, [editor, onEditorReady])

  useEffect(() => {
    if (!editor || (!onBodyImageRemoved && !onBodyImageAdded)) return

    const handleTransaction = ({ transaction }) => {
      if (!transaction.docChanged) return

      const oldImages = []
      const newImages = []

      transaction.before.descendants((node) => {
        if (node.type.name === 'image' && node.attrs['data-body-image-id']) {
          oldImages.push(node.attrs['data-body-image-id'])
        }
      })

      transaction.doc.descendants((node) => {
        if (node.type.name === 'image' && node.attrs['data-body-image-id']) {
          newImages.push(node.attrs['data-body-image-id'])
        }
      })

      const removedIds = oldImages.filter((id) => !newImages.includes(id))
      if (onBodyImageRemoved) {
        removedIds.forEach((bodyImageId) => {
          onBodyImageRemoved(bodyImageId)
        })
      }

      if (!onBodyImageAdded || isApplyingBodyImageIdsRef.current) return

      const addedImages = []
      const seenBodyImageIds = new Set()
      const cdnUrl = apiConfigs.cndUrl?.replace(/\/$/, '')
      isApplyingBodyImageIdsRef.current = true
      editor.commands.command(({ tr, state }) => {
        let updated = false
        state.doc.descendants((node, pos) => {
          if (node.type.name !== 'image') return
          const existingBodyImageId = node.attrs['data-body-image-id']
          const src = node.attrs.src
          if (existingBodyImageId) {
            if (seenBodyImageIds.has(existingBodyImageId)) {
              if (!src || !cdnUrl || !src.startsWith(cdnUrl)) return
              const bodyImageId = generateBodyImageId()
              tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                'data-body-image-id': bodyImageId,
              })
              addedImages.push({ bodyImageId, src })
              updated = true
              return
            }
            seenBodyImageIds.add(existingBodyImageId)
            return
          }
          if (!src || !cdnUrl || !src.startsWith(cdnUrl)) return
          const bodyImageId = generateBodyImageId()
          tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            'data-body-image-id': bodyImageId,
          })
          addedImages.push({ bodyImageId, src })
          updated = true
        })
        return updated
      })
      isApplyingBodyImageIdsRef.current = false
      addedImages.forEach((image, index) => {
        const normalizedUrl = normalizeBodyImageUrl(image.src)
        const name =
          normalizedUrl?.split('/').pop()?.split('?')[0] ||
          `pasted-image-${index + 1}`
        onBodyImageAdded({
          bodyImageId: image.bodyImageId,
          url: normalizedUrl,
          name,
        })
      })
    }

    editor.on('transaction', handleTransaction)

    return () => {
      editor.off('transaction', handleTransaction)
    }
  }, [editor, onBodyImageAdded, onBodyImageRemoved, normalizeBodyImageUrl])

  const removeImagesByUrl = useCallback(
    (urlsToRemove) => {
      urlsToRemove.forEach((url) => {
        editor.commands.command(({ tr, state }) => {
          const { doc } = state
          let found = false

          doc.descendants((node, pos) => {
            if (node.type.name === 'image' && node.attrs.src === url) {
              tr.delete(pos, pos + node.nodeSize)
              found = true
            }
          })

          return found
        })
      })
    },
    [editor],
  )

  useEffect(() => {
    if (!editor) return

    const handlePaste = async (event) => {
      const clipboardText = event.clipboardData?.getData('text/plain')
      const files = event.clipboardData?.files
      const html = event.clipboardData?.getData('text/html')

      // youtube
      if (clipboardText) {
        const youtubeRegex =
          /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]+)/
        const match = clipboardText.match(youtubeRegex)

        if (match && match[1]) {
          event.preventDefault()
          event.stopPropagation()

          const videoId = match[1]
          const pageOrigin = 'https://messagespring.com'
          const videoUrl = `https://www.youtube.com/embed/${videoId}?rel=0&enablejsapi=1&origin=${pageOrigin}`

          const currentJson = editor.getJSON()

          currentJson.content = currentJson.content.filter(
            (node) => !(node.type === 'youtube' && !node.attrs.videoId),
          )

          currentJson.content.push(
            {
              type: 'youtube',
              attrs: {
                src: videoUrl,
                videoId,
              },
            },
            { type: 'paragraph' },
          )

          editor.commands.setContent(currentJson, true)
          return
        }
      }

      // Handle files (images/videos/docs)
      if (files && files.length > 0) {
        event.preventDefault()

        let imageUrlsToRemove = []
        if (html && html.includes('<img')) {
          const imgRegex = /<img[^>]*src="([^"]*)"[^>]*>/g
          let match
          while ((match = imgRegex.exec(html)) !== null) {
            imageUrlsToRemove.push(match[1])
          }
        }

        // Upload each file based on its type
        for (const file of files) {
          if (file.type.startsWith('image/')) {
            await uploadImage(file)
            removeImagesByUrl(imageUrlsToRemove)
          } else if (file.type.startsWith('video/')) {
            await uploadVideo(file)
          } else {
            await uploadFile(file)
          }
        }
        return
      }

      // Handle pasted images in HTML content
      if (!html || !html.includes('<img')) {
        return
      }

      const imgRegex = /<img[^>]*src="([^"]*)"[^>]*>/g
      const images = []
      let match

      while ((match = imgRegex.exec(html)) !== null) {
        images.push(match[1])
      }

      const imagesToUpload = images.filter((src) => {
        return src.startsWith('http') || src.startsWith('data:image/')
      })

      console.log('Images to upload:', imagesToUpload)

      if (imagesToUpload.length === 0) {
        return // No images need uploading, let TipTap handle normally
      }
      setTimeout(() => {
        uploadAndReplaceImages(imagesToUpload)
      }, 100) // Small delay to let paste complete
    }

    editor.view.dom.addEventListener('paste', handlePaste)

    return () => {
      editor.view.dom.removeEventListener('paste', handlePaste)
    }
  }, [editor])

  const {
    exec,
    handleColorChange,
    handleFontFamily,
    handleFontSize,
    handleLinkClick,
    handleInsertTable,
    handleAddColumnAfter,
    uploadImage,
    uploadExternalImages,
    uploadVideo,
    uploadFile,
  } = useToolbarHandlers(
    editor,
    onUploading,
    onUploaded,
    onChangeImage,
    place,
    onEditorLoading,
    setEditorErrorMessage,
    formatMessage,
    accessToken,
    onBodyImageAdded,
  )

  const uploadAndReplaceImages = useCallback(
    async (imagesToUpload) => {
      const results = await uploadExternalImages(imagesToUpload)

      const failedImages = results?.filter((res) => !res.success)
      const successfulImages = results?.filter((res) => res.success)

      const parser = new DOMParser()
      let currentHtml = editor.getHTML()
      successfulImages.forEach((result) => {
        currentHtml = currentHtml.replace(result.originalUrl, result.cdnUrl)
      })

      const doc = parser.parseFromString(currentHtml, 'text/html')

      // Handle failed images - remove the wrapper div (parent of img)
      failedImages.forEach((result) => {
        const img = doc.querySelector(`img[src="${result.originalUrl}"]`)
        if (img) {
          const wrapper = img.parentElement
          if (wrapper) {
            wrapper.remove()
          } else {
            // Fallback: just remove the image if no wrapper
            img.remove()
          }
        }
      })

      let finalHtml = doc.body.innerHTML

      if (finalHtml.indexOf(apiConfigs.cndUrl) > -1) {
        finalHtml = removeImageKitStyle(finalHtml)
      }
      const styledHtml = applyImageStyling(finalHtml)

      editor.commands.setContent(styledHtml, true)

      // Show error if any failed
      if (failedImages?.length > 0) {
        const errorMsg = formatMessage({ id: 'tiptap.imagepaste.fail.message' })
        setErrorMessage(errorMsg)
      }
    },
    [editor, uploadExternalImages],
  )

  const handleTextMoreOptions = (val) => {
    switch (val) {
      case 'subscript':
        exec('toggleSubscript')
        break
      case 'superscript':
        exec('toggleSuperscript')
        break
      case 'html_source':
        toggleHtmlSource()
        break
      default:
        console.log('Add items for trigger action')
        break
    }
  }

  const handleExitLink = useCallback(() => {
    if (editor?.isActive('link')) {
      const { to } = editor.state.selection
      editor.chain().focus().setTextSelection(to).insertContent(' ').run()
      editor.chain().focus().unsetMark('link').run()
    }
  }, [editor])

  const toggleHtmlSource = useCallback(() => {
    setShowHtmlSource(!showHtmlSource)
  }, [showHtmlSource])

  const handleLineHeightSelect = useCallback(
    (lineHeight) => {
      if (lineHeight === '1') {
        editor?.chain().focus().unsetLineHeight().run()
      } else {
        editor?.chain().focus().setLineHeight(lineHeight).run()
      }
    },
    [editor],
  )

  const handleTextAlignSelect = useCallback(
    (alignment) => {
      switch (alignment) {
        case 'left':
          exec('setTextAlign', ['left'])
          break
        case 'center':
          exec('setTextAlign', ['center'])
          break
        case 'right':
          exec('setTextAlign', ['right'])
          break
        case 'justify':
          exec('setTextAlign', ['justify'])
          break
        default:
          exec('setTextAlign', ['left'])
          break
      }
    },
    [exec],
  )

  const getDefaultFontFamilyFontSize = () => {
    const element = document.querySelector('.ProseMirror')
    if (!element) return ''

    const style = window.getComputedStyle(element)
    const size = style.fontSize || '14px'
    const firstFont = style?.fontFamily.split(',')?.[0]
    const family = firstFont.replace(/["']/g, '').trim() || 'default'
    return [family, size]
  }

  const [defaultFont, defaultSize] = getDefaultFontFamilyFontSize()

  const fontFamilyOptions = getFontFamily(defaultFont)

  const currentFontFamily =
    editor?.getAttributes('textStyle').fontFamily || defaultFont
  const currentFontSize =
    editor?.getAttributes('textStyle')?.fontSize || defaultSize
  const currentLineHeight =
    editor?.getAttributes('paragraph')?.lineHeight || '1'

  const isVisuallyEmpty = () => {
    const json = editor?.getJSON()
    const content = json?.content ?? []

    return (
      content.length === 0 ||
      (content.length === 1 &&
        content[0].type === 'paragraph' &&
        content[0].content === undefined)
    )
  }

  const validateFile = (file) => {
    const category = file.type.startsWith('image/')
      ? 'image'
      : file.type.startsWith('video/')
        ? 'video'
        : 'file'
    const config = VALIDATION_CONFIG[category]
    const errors = []
    if (!config.types.includes(file.type)) {
      errors.push(`${file.name}: ${file.type} not supported`)
    }
    if (file.size > config.maxSize) {
      errors.push(
        `${file.name} too large : (${(file.size / 1024 / 1024).toFixed(1)}MB > ${config.maxSize / 1024 / 1024}MB)`,
      )
    }
    return { isValid: errors.length === 0, errors, category }
  }

  const handleFileSelect = async (event) => {
    event.preventDefault()
    const file = event.target.files[0]

    if (!file) return

    const validation = validateFile(file)
    event.target.value = ''
    if (validation.isValid) {
      if (validation.category === 'image') {
        await uploadImage(file)
      } else if (validation.category === 'video') {
        await uploadVideo(file)
      } else {
        await uploadFile(file)
      }
    } else {
      if (validation?.errors?.length) {
        setErrorMessage(validation.errors.map((err) => err).join(', '))
      } else {
        setErrorMessage(`Unsupported file types`)
      }
    }
  }

  const handleDrop = async (event) => {
    event.preventDefault()

    const files = Array.from(event.dataTransfer.files)

    if (files.length === 0) return

    const validFiles = []
    const invalidFiles = []

    files.forEach((file) => {
      const validation = validateFile(file)
      if (validation.isValid) {
        validFiles.push(file)
      } else {
        invalidFiles.push({ file, errors: validation.errors })
      }
    })

    if (invalidFiles.length > 0) {
      setErrorMessage(
        invalidFiles.map((item) => `${item.errors.join(', ')}`).join(', '),
      )
    } else {
      for (const file of files) {
        if (file.type.startsWith('image/')) {
          await uploadImage(file)
        } else if (file.type.startsWith('video/')) {
          await uploadVideo(file)
        } else {
          await uploadFile(file)
        }
      }
    }
  }

  return (
    <>
      <style>{`
      div[data-youtube-video] {
            position: relative;
            width: 100% !important; /* force full width */
            padding-bottom: 56.25%; /* 16:9 ratio */
            height: 0;
            overflow: hidden;
          }

          div[data-youtube-video] > iframe {
            position: absolute;
            top: 0;
            left: 0;
            width: 100% !important;
            height: 100% !important;
          }

          .ProseMirror {
            word-break: break-word !important;
            overflow-wrap: anywhere;
          }
          .ProseMirror table {
            border-collapse: collapse;
            margin: 10px 0;
            overflow: hidden;
            table-layout: fixed;
            border: 1px solid #C5C5C5;
            position: relative;
            min-width: 200px;
            max-width: 100%;
            min-height: 100px;
            display: contents
          }

          .ProseMirror td, .ProseMirror th {
            border: 1px solid #C5C5C5;
            box-sizing: border-box;
            min-width: 80px;
            padding: 8px 12px;
            position: relative;
            max-width: 300px;
            vertical-align: top;
            background: white;
            overflow: hidden;
          }

          .ProseMirror th {
            background-color: #f8f9fa;
            font-weight: 600;
            text-align: left;
          }

          .ProseMirror .selectedCell {
            background: rgba(59, 130, 246, 0.1);
            border: 2px solid #3b82f6;
          }

          .ProseMirror table p {
            margin: 0;
          }

          /* Column resize handles */
          .ProseMirror .column-resize-handle {
            bottom: -2px;
            position: absolute;
            right: -2px;
            top: 0;
            width: 4px;
            cursor: col-resize;
            pointer-events: all;
          }

          .ProseMirror.resize-cursor {
            cursor: ew-resize;
            cursor: col-resize;
          }

          .ProseMirror td:hover .column-resize-handle,
          .ProseMirror th:hover .column-resize-handle {
            background-color: #1d4ed8;
          }

          /* Add this to your existing <style> block */
          .ProseMirror .custom-blockquote {
            border-left: 4px solid #d1d5db;
            margin: 1.5rem 0;
            padding: 0.75rem 1.25rem;
            background-color: #f8fafc;
            border-radius: 0 8px 8px 0;
            font-style: italic;
            color: #475569;
            word-break: break-word;
            overflow-wrap: anywhere;
            position: relative;
          }

          .ProseMirror .custom-blockquote::before {
            content: '"';
            font-size: 3rem;
            color: #cbd5e1;
            position: absolute;
            top: -0.5rem;
            left: 0.5rem;
            font-family: serif;
          }

          .ProseMirror .custom-blockquote p {
            margin: 0.5rem 0;
            font-size: 1.1em;
            line-height: 1.6;
          }

          .ProseMirror .custom-blockquote p:first-child {
            margin-top: 0;
          }

          .ProseMirror .custom-blockquote p:last-child {
            margin-bottom: 0;
          }
      `}</style>
      <div
        className={`relative border rounded-8 bg-white ${error ? 'border-red-500' : 'border-grey-input'} ${className}`}
      >
        {isLoading && (
          <div className="absolute rounded-8 inset-0 bg-black bg-opacity-30 flex items-center justify-center z-20">
            <MessagespringLoading />
          </div>
        )}

        {!isEmptyOrNil(errorMessage) && (
          <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center z-30">
            <div className="bg-white rounded-md shadow-lg p-6 sm:max-w-sm w-full text-center py-5">
              <Typography className="text-gray-700 py-5 mb-4">
                {errorMessage}
              </Typography>
              <OutlinedButtons
                id="add-group-btn"
                type="button"
                tabIndex={-1}
                color={CLOSE}
                onClick={() => setErrorMessage(undefined)}
              >
                <FormattedMessage id="text.close" />
              </OutlinedButtons>
            </div>
          </div>
        )}

        {/* Full Toolbar */}
        <div className="sticky top-0 z-10 border-b border-grey-input rounded-t-8 bg-white">
          <div className="flex flex-wrap gap-4">
            <div className="flex w-auto border-r-1 border-gray-200 px-4 py-2 ">
              <ToolbarButton
                onClick={() => exec('toggleBold')}
                isActive={editor?.isActive('bold')}
                title="Bold"
                disabled={isLoading}
              >
                <FormatBoldIcon
                  className={clsx(
                    'text-20',
                    editor?.isActive('bold')
                      ? 'text-secondary'
                      : 'text-grey-400',
                  )}
                />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => exec('toggleItalic')}
                isActive={editor?.isActive('italic')}
                title="Italic"
                disabled={isLoading}
              >
                <FormatItalicIcon
                  className={clsx(
                    'text-20',
                    editor?.isActive('italic')
                      ? 'text-secondary'
                      : 'text-grey-400',
                  )}
                />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => exec('toggleUnderline')}
                isActive={editor?.isActive('underline')}
                title="Underline"
                disabled={isLoading}
              >
                <FormatUnderlinedIcon
                  className={clsx('text-20', {
                    'text-secondary': editor?.isActive('underline'),
                  })}
                />
              </ToolbarButton>
            </div>

            <div className="flex w-auto border-r-1 items-center border-gray-200 px-4 py-2 ">
              <SimpleTiptapDropdown
                mode="dropdown"
                value={currentFontFamily}
                onChange={handleFontFamily}
                title="Font Family"
                isActive={currentFontFamily !== defaultFont}
                disabled={isLoading}
              >
                {fontFamilyOptions.map((opt, index) => (
                  <SimpleTiptapDropdown.Option key={index} value={opt.value}>
                    {opt.label}
                  </SimpleTiptapDropdown.Option>
                ))}
              </SimpleTiptapDropdown>

              <SimpleTiptapDropdown
                mode="dropdown"
                value={currentFontSize}
                onChange={handleFontSize}
                title="Font Size"
                isActive={currentFontSize !== defaultSize}
                disabled={isLoading}
              >
                {fontSizeOptions.map((opt, index) => (
                  <SimpleTiptapDropdown.Option key={index} value={opt.value}>
                    {opt.label}
                  </SimpleTiptapDropdown.Option>
                ))}
              </SimpleTiptapDropdown>
            </div>

            <div className="flex w-auto border-r-1 border-gray-200 px-4 py-2 ">
              <button
                type="button"
                onClick={() => document.getElementById('color-input').click()}
                className="p-2 rounded hover:bg-gray-200 flex items-center justify-center relative"
                title="Text Color"
                disabled={isLoading}
              >
                <FormatColorTextOutlinedIcon className="text-grey-400 text-20" />
                <input
                  id="color-input"
                  type="color"
                  onChange={handleColorChange}
                  className="absolute opacity-0 w-full h-full cursor-pointer"
                  style={{ position: 'absolute', top: 0, left: 0 }}
                  disabled={isLoading}
                />
              </button>

              <ToolbarButton
                onClick={handleInsertTable}
                isActive={editor?.isActive('table')}
                title="Insert Table"
                disabled={isLoading}
              >
                <BorderAllIcon
                  className={clsx('text-20', {
                    'text-secondary': editor?.isActive('table'),
                  })}
                />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => {
                  editor
                    ?.chain()
                    .focus()
                    .unsetAllMarks()
                    .clearNodes()
                    .unsetTextAlign()
                    .run()
                }}
                isActive={false}
                title="Clear Formatting"
                disabled={isLoading}
              >
                <FormatClearIcon className="text-20" />
              </ToolbarButton>

              <ToolbarButton
                onClick={() => exec('toggleBlockquote')}
                isActive={editor?.isActive('blockquote')}
                title="Blockquote (Type > at start of line)"
                disabled={isLoading}
              >
                <FormatQuoteOutlinedIcon
                  className={clsx('text-20', {
                    'text-secondary': editor?.isActive('blockquote'),
                  })}
                />
              </ToolbarButton>

              <ToolbarButton
                onClick={() => editor.commands.setHorizontalRule()}
                title="Insert Horizontal Line"
                disabled={isLoading}
              >
                <RemoveIcon className="text-20" />
              </ToolbarButton>

              <SimpleTiptapDropdown
                mode="options"
                icon={<MoreHorizIcon />}
                onSelect={handleTextMoreOptions}
                disabled={isLoading}
                title="More Options"
              >
                <SimpleTiptapDropdown.Option
                  value="subscript"
                  key="subscript"
                  isActive={editor?.isActive('subscript')}
                >
                  <div className="flex items-center p-1 gap-2">
                    <div title="Subscript" disabled={isLoading}>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6.34959 8.94171L3.66626 4.76671C3.26626 4.14171 3.70793 3.33337 4.44126 3.33337C4.76626 3.33337 5.05793 3.50004 5.23293 3.77504L7.53293 7.49171H7.63293L9.91626 3.78337C10.0913 3.50004 10.3913 3.33337 10.7163 3.33337C11.4496 3.33337 11.8996 4.15004 11.4996 4.76671L8.80793 8.93337L11.7663 13.5584C12.1663 14.1834 11.7163 15 10.9829 15C10.6663 15 10.3663 14.8334 10.1913 14.5667L7.63293 10.4917H7.53293L4.97459 14.5667C4.80793 14.8334 4.50793 15 4.18293 15C3.44959 15 2.99959 14.1917 3.39959 13.5667L6.34959 8.94171ZM16.7496 16.25C16.7496 16.0167 16.5663 15.8334 16.3329 15.8334H14.2496V15H15.9163C16.3746 15 16.7496 14.625 16.7496 14.1667V13.3334C16.7496 12.875 16.3746 12.5 15.9163 12.5H13.8329C13.5996 12.5 13.4163 12.6834 13.4163 12.9167C13.4163 13.15 13.5996 13.3334 13.8329 13.3334H15.9163V14.1667H14.2496C13.7913 14.1667 13.4163 14.5417 13.4163 15V15.8334C13.4163 16.2917 13.7913 16.6667 14.2496 16.6667H16.3329C16.5663 16.6667 16.7496 16.4834 16.7496 16.25Z"
                          fill="#404040"
                        />
                      </svg>
                    </div>
                    Subscript
                  </div>
                </SimpleTiptapDropdown.Option>
                <SimpleTiptapDropdown.Option
                  value="superscript"
                  key="superscript"
                  isActive={editor?.isActive('superscript')}
                >
                  <div className="flex items-center p-1 gap-2">
                    <div title="Superscript" disabled={isLoading}>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6.34126 10.6084L3.66626 6.43337C3.26626 5.80837 3.70793 5.00004 4.44126 5.00004C4.76626 5.00004 5.05793 5.16671 5.23293 5.44171L7.53293 9.15837H7.63293L9.91626 5.45004C10.0829 5.16671 10.3829 5.00004 10.7079 5.00004C11.4413 5.00004 11.8913 5.81671 11.4913 6.43337L8.79959 10.6L11.7579 15.225C12.1663 15.85 11.7163 16.6667 10.9829 16.6667C10.6663 16.6667 10.3663 16.5 10.1913 16.2334L7.63293 12.1584H7.53293L4.97459 16.2334C4.79959 16.5 4.50793 16.6667 4.18293 16.6667C3.44959 16.6667 2.99959 15.8584 3.39959 15.2334L6.34126 10.6084ZM16.7496 7.08337C16.7496 6.85004 16.5663 6.66671 16.3329 6.66671H14.2496V5.83337H15.9163C16.3746 5.83337 16.7496 5.45837 16.7496 5.00004V4.16671C16.7496 3.70837 16.3746 3.33337 15.9163 3.33337H13.8329C13.5996 3.33337 13.4163 3.51671 13.4163 3.75004C13.4163 3.98337 13.5996 4.16671 13.8329 4.16671H15.9163V5.00004H14.2496C13.7913 5.00004 13.4163 5.37504 13.4163 5.83337V6.66671C13.4163 7.12504 13.7913 7.50004 14.2496 7.50004H16.3329C16.5663 7.50004 16.7496 7.31671 16.7496 7.08337Z"
                          fill="#404040"
                        />
                      </svg>
                    </div>
                    Superscript
                  </div>
                </SimpleTiptapDropdown.Option>
                {['local', 'dev', 'qa'].includes(appInfo.appEnv) && (
                  <SimpleTiptapDropdown.Option
                    value="html_source"
                    key="html_source"
                    isActive={showHtmlSource}
                  >
                    <div className="flex items-center p-1 gap-2">
                      <div className="flex items-center p-1 gap-2">
                        <div title="View HTML Source" disabled={isLoading}>
                          <CodeIcon
                            className={clsx('text-20', {
                              'text-secondary': showHtmlSource,
                            })}
                          />
                        </div>
                      </div>
                      View HTML Source
                    </div>
                  </SimpleTiptapDropdown.Option>
                )}
              </SimpleTiptapDropdown>
            </div>
            <div className="flex w-auto border-r-1 border-gray-200 px-4 py-2 items-center">
              <SimpleTiptapDropdown
                mode="options"
                showArrow={true}
                onSelect={handleTextAlignSelect}
                title="Text Alignment"
                disabled={isLoading}
                icon={
                  editor?.isActive({ textAlign: 'left' }) ? (
                    <FormatAlignLeftIcon className="text-20" />
                  ) : editor?.isActive({ textAlign: 'center' }) ? (
                    <FormatAlignCenterIcon className="text-20" />
                  ) : editor?.isActive({ textAlign: 'right' }) ? (
                    <FormatAlignRightIcon className="text-20" />
                  ) : editor?.isActive({ textAlign: 'justify' }) ? (
                    <FormatAlignJustifyIcon className="text-20" />
                  ) : (
                    <FormatAlignLeftIcon className="text-20" />
                  )
                }
              >
                <SimpleTiptapDropdown.Option
                  value="left"
                  key="left"
                  isActive={editor?.isActive({ textAlign: 'left' })}
                >
                  <div className="flex items-center p-1 gap-2">
                    <FormatAlignLeftIcon className="text-20" />
                    Align Left
                  </div>
                </SimpleTiptapDropdown.Option>

                <SimpleTiptapDropdown.Option
                  value="center"
                  key="center"
                  isActive={editor?.isActive({ textAlign: 'center' })}
                >
                  <div className="flex items-center p-1 gap-2">
                    <FormatAlignCenterIcon className="text-20" />
                    Align Center
                  </div>
                </SimpleTiptapDropdown.Option>

                <SimpleTiptapDropdown.Option
                  value="right"
                  key="right"
                  isActive={editor?.isActive({ textAlign: 'right' })}
                >
                  <div className="flex items-center p-1 gap-2">
                    <FormatAlignRightIcon className="text-20" />
                    Align Right
                  </div>
                </SimpleTiptapDropdown.Option>

                <SimpleTiptapDropdown.Option
                  value="justify"
                  key="justify"
                  isActive={editor?.isActive({ textAlign: 'justify' })}
                >
                  <div className="flex items-center p-1 gap-2">
                    <FormatAlignJustifyIcon className="text-20" />
                    Justify
                  </div>
                </SimpleTiptapDropdown.Option>
              </SimpleTiptapDropdown>

              <SimpleTiptapDropdown
                mode="options"
                icon={<FormatLineSpacingIcon />}
                onSelect={handleLineHeightSelect}
                title="Line Height"
                disabled={isLoading}
              >
                {lineHeightOptions.map((opt) => (
                  <SimpleTiptapDropdown.Option
                    key={opt.value}
                    value={opt.value}
                    isActive={currentLineHeight === opt.value}
                  >
                    {opt.label}
                  </SimpleTiptapDropdown.Option>
                ))}
              </SimpleTiptapDropdown>

              <ToolbarButton
                onClick={() => exec('toggleBulletList')}
                isActive={editor?.isActive('bulletList')}
                title="Bullet List"
                disabled={isLoading}
              >
                <FormatListBulletedIcon
                  className={clsx('text-20', {
                    'text-secondary': editor?.isActive('bulletList'),
                  })}
                />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => exec('toggleOrderedList')}
                isActive={editor?.isActive('orderedList')}
                title="Numbered List"
                disabled={isLoading}
              >
                <FormatListNumberedIcon
                  className={clsx('text-20', {
                    'text-secondary': editor?.isActive('orderedList'),
                  })}
                />
              </ToolbarButton>
              <ToolbarButton
                onClick={() =>
                  editor
                    ?.chain()
                    .focus()
                    .insertContent('\u00A0\u00A0\u00A0\u00A0')
                    .run()
                }
                title="Insert Tab"
                disabled={isLoading}
              >
                <FormatIndentIncreaseIcon className="text-20" />
              </ToolbarButton>
            </div>
            <div className="flex w-auto border-r-1 border-gray-200 px-4 py-2">
              <ToolbarButton
                onClick={handleLinkClick}
                isActive={editor?.isActive('link')}
                title="Insert/Edit Link"
                disabled={isLoading}
              >
                <LinkIcon
                  className={clsx('text-20', {
                    'text-secondary': editor?.isActive('link'),
                  })}
                />
              </ToolbarButton>
              {editor?.isActive('link') && (
                <ToolbarButton
                  onClick={handleExitLink}
                  isActive={false}
                  title="Exit Link Mode"
                  className="text-red-600 relative"
                  disabled={isLoading}
                >
                  <LinkOffOutlinedIcon className="text-20" />
                </ToolbarButton>
              )}
              <button
                type="button"
                onClick={() => document.getElementById('image-input').click()}
                className="p-2 rounded hover:bg-gray-200 flex items-center justify-center"
                title="Insert Image"
                disabled={isLoading}
              >
                <ImageOutlinedIcon className="text-grey-400 text-20" />
              </button>
              <button
                type="button"
                onClick={() => document.getElementById('video-input').click()}
                className="p-2 rounded hover:bg-gray-200 flex items-center justify-center"
                title="Insert Video"
                disabled={isLoading}
              >
                <VideocamOutlinedIcon className="text-grey-400 text-20" />
              </button>
              <button
                type="button"
                onClick={() => document.getElementById('file-input').click()}
                className="p-2 rounded hover:bg-gray-200 flex items-center justify-center"
                title="Insert File"
                disabled={isLoading}
              >
                <PostAddOutlinedIcon className="text-grey-400 text-20" />
              </button>
            </div>

            <div className="flex w-auto px-4 py-2">
              <ToolbarButton
                onClick={() => exec('toggleNoTranslate')}
                isActive={editor?.isActive('noTranslate')}
                title="No Translate"
                disabled={isLoading}
              >
                <div className="w-20 h-20 text-gray-700">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    className="w-full h-full"
                  >
                    <path
                      fill={
                        editor?.isActive('noTranslate') ? '#00B2AD' : '#646464'
                      }
                      d="M4.123 5.189 3 6.312l2.905 2.905v6.009a1.325 1.325 0 0 0 1.321 1.321h5.283l.66 1.981h2.047l2.047 2.047 1.123-1.123-14.065-14-.198-.263Zm2.443 5.415a2.036 2.036 0 0 1 .066-.66l1.254 1.254a2.2 2.2 0 0 0 1.519 1.519l1.189 1.189a2.247 2.247 0 0 1-.66.066 3.381 3.381 0 0 1-3.368-3.368ZM11.122 11.132l.528.528c.075-.167.12-.346.132-.528h-.66Z"
                    />
                    <path
                      fill={
                        editor?.isActive('noTranslate') ? '#00B2AD' : '#646464'
                      }
                      d="M19.112 5.981h-6.607L11.848 4H7.226a1.325 1.325 0 0 0-1.321 1.321v.594l1.981 2.047A3.2 3.2 0 0 1 9.805 7.3a3.26 3.26 0 0 1 2.245.858l-.858.858a1.76 1.76 0 0 0-1.325-.526 2.226 2.226 0 0 0-1.123.33l1.189 1.189h2.972c.058.215.08.438.066.66a3.378 3.378 0 0 1-.528 1.849l3.236 3.236-.66-1.915 2.047 2.047.594-.594-2.114-2.179a7.773 7.773 0 0 0 1.585-2.773h1.254v-.859h-2.971v-.859h-.859v.858h-.924l-.858-2.509h5.942a.662.662 0 0 1 .66.66v9.245a.662.662 0 0 1-.66.66h-1.188l.99.99h.594a1.325 1.325 0 0 0 1.321-1.321V7.3a1.325 1.325 0 0 0-1.32-1.319Zm-2.774 4.358a7.448 7.448 0 0 1-1.32 2.311 7.015 7.015 0 0 1-.594-.726l-.529-1.585h2.443Z"
                    />
                  </svg>
                </div>
              </ToolbarButton>
            </div>

            {/* History Controls */}
            <div className="flex w-auto border-l-1 border-gray-200 px-2 py-2">
              <ToolbarButton
                onClick={() => exec('undo')}
                isActive={false}
                title="Undo (Ctrl+Z)"
                disabled={isLoading || !editor?.can().undo()}
              >
                <UndoIcon className="text-20" />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => exec('redo')}
                isActive={false}
                title="Redo (Ctrl+Y)"
                disabled={isLoading || !editor?.can().redo()}
              >
                <RedoIcon className="text-20" />
              </ToolbarButton>
            </div>
          </div>
        </div>

        {/* Editor Content */}
        <div className="relative">
          {showHtmlSource ? (
            <textarea
              value={editor?.getHTML() || ''}
              onChange={(e) => {
                if (editor) {
                  editor.commands.setContent(e.target.value)
                }
              }}
              disabled="true"
              className="w-full min-h-[300px] max-h-[75vh] p-4 font-mono border-none resize-none focus:outline-none overflow-y-auto"
              style={{ fontSize: '14px' }}
              placeholder="HTML source code..."
            />
          ) : (
            <>
              <EditorContent
                onDrop={handleDrop}
                editor={editor}
                className="min-h-[300px] max-w-[95vw] lg:max-w-[63vw] p-10 max-h-[75vh] overflow-y-auto"
                data-type="editor"
              />

              {/* Enhanced Placeholder */}
              {isVisuallyEmpty() && placeholder && (
                <div className="absolute top-4 left-4 text-gray-400 pointer-events-none p-10 select-none">
                  {placeholder}
                </div>
              )}
            </>
          )}
        </div>

        {editor && (
          <>
            <TableBubbleMenu
              editor={editor}
              handleAddColumnAfter={handleAddColumnAfter}
            />
          </>
        )}

        {/* Footer */}
        <div className="flex justify-between items-center px-4 py-2 border-t border-gray-200 text-gray-400 rounded-b-8 text-14">
          {toolbarItems && <div>{toolbarItems}</div>}

          <div className="px-4">
            <span>
              <FormattedMessage id="tiptap.character.count" />{' '}
              {editor?.getText().length || 0} / {maxLength || '∞'}
            </span>
          </div>
        </div>
      </div>
      {error && (
        <div className="flex space-x-2 py-2">
          <div className="flex my-auto">
            <ErrorOutlineIcon className="text-red-500" fontSize="small" />
          </div>
          <label className="text-red text-14">{errorText}</label>
        </div>
      )}

      <input
        id="image-input"
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      <input
        id="file-input"
        type="file"
        accept="*/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      <input
        id="video-input"
        type="file"
        accept="video/mp4, audio/mpeg, audio/wav"
        onChange={handleFileSelect}
        className="hidden"
      />
    </>
  )
}

export default TiptapEditor
